// src/pages/PrivacyPolicyPage.jsx
//
// Rewritten 2026-09-08 for the seven markets the app serves (Australia, New
// Zealand, India, the UK, Ireland, Canada, the US) and, more importantly, to
// be true to what the app actually does now:
//   - anonymous identity from the DDD auth hub (no name or email needed),
//     with tool writing and results backed up under a random ID whenever the
//     app has a hub session
//   - accounts made on signup.html are THIS project's own Firebase Auth, not
//     the hub, and their tool writing is not backed up (HubSessionService.js)
//   - device storage is plain; server storage is encrypted in transit and at
//     rest on Google Cloud in Sydney — NOT end-to-end, NOT zero-knowledge
//   - Firebase Analytics and Performance DO run today (services/config/
//     firebase.js); no ads; nothing sold. Draft PR #602 removes them, per
//     docs/ANALYTICS_STANDARD.md (teen tier); update this page when it lands
//   - Friend Circles (since 2 October 2026, docs/CIRCLES_PULSE.md): no writing
//     at all; what a circle stores is listed exactly; question answers are
//     NAMED once revealed (never call them anonymous); leaving deletes your
//     answers and your name from the circle; nobody can remove anyone; the
//     old written reflections, comments and Question of the Day answers are
//     no longer readable and are being deleted
//   - the Coach: messages go to the Knowledge Hub and on to Anthropic's Claude
//     models for the reply; deletable from the Coach page
//   - export, and deleting from the device, the hub backup and the Coach, are
//     self-serve on Privacy & Data (services/DeleteMyDataService.js); leaving
//     a circle deletes your answers in it; account deletion is by email (the
//     hub has no self-serve deletion yet)
// Updated 2 October 2026 to match the code (docs/REVIEW_2026-10.md, P0-7).
// Jurisdiction lines follow the house rule: Australia as the worked example
// beside a generalisation; never Australian law presented as universal.

import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield, ChevronLeft, Lock, Eye, Database, UserCheck, Check, X, Circle,
  UserCog, Info, Globe, FileText, Trash2, Clock, Users,
  Baby, Mail, ExternalLink,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import Card from '../components/shared/Card';

const HELP = 'help@divedeepdialogues.com';

const bullets = (items, Icon = Circle, cls = 'w-3 h-3 text-purple-300 mt-1.5 shrink-0') => (
  <ul className="space-y-2 text-slate-300">
    {items.map((item) => (
      <li key={typeof item === 'string' ? item : item.key} className="flex items-start gap-2">
        <Icon className={cls} />
        <span>{typeof item === 'string' ? item : item.node}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPolicyPage = () => {
  const { setCurrentView } = useApp();

  const shortVersion = [
    {
      icon: UserCheck,
      title: 'You can use it without telling us who you are',
      description: 'The app gives you a random ID. No name, no email address, nothing that says it is you. If you want an account, a username and password is enough.',
    },
    {
      icon: Lock,
      title: 'Your writing is backed up under that random ID',
      description: 'It lives on your device first, and is backed up to our servers in Sydney so it survives clearing your browser. It is encrypted on the way and while stored, but it is not end-to-end encrypted: we could technically read it. We do not. (Accounts made on the sign-up page are not backed up this way.)',
    },
    {
      icon: Eye,
      title: 'Some usage analytics, no ads, nothing sold',
      description: "Google's Firebase Analytics and Performance Monitoring run in the app today: they record which screens are opened and a few events, as described below. None of it is for advertising and nothing is sold. Our own standard for apps used by teenagers says they should not run here, and we are removing them.",
    },
    {
      icon: Trash2,
      title: 'You can download it and delete it',
      description: 'Download what you have written from Privacy & Data, and delete it there: that removes it from your device, from the backup on our servers and from the Coach. Leaving a circle deletes your answers in it. To delete your account, email us.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <button
            type="button"
            onClick={() => setCurrentView('home')}
            className="flex items-center gap-2 text-purple-300 hover:text-white mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </button>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-lg text-slate-300">Friendships Vibe, a Dive Deep Dialogues app</p>
            <p className="text-sm text-slate-400 mt-2">Last updated: 3 October 2026</p>
          </div>

          {/* Who we are */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Who we are, and who this is for</h2>
            <p className="text-slate-300 mb-3">
              Friendships Vibe is made by SILVER WOLF Productions (ABN 66 376 765 633), the Australian studio behind
              Dive Deep Dialogues. We are the ones responsible for your information under this policy, and you can
              reach us at <a href={`mailto:${HELP}`} className="text-purple-300 hover:underline">{HELP}</a>.
            </p>
            <p className="text-slate-300">
              People use this app from Australia, New Zealand, India, the UK, Ireland, Canada and the US. Privacy law
              is different in each, so where it matters we give the Australian rule as the example and say what to
              expect elsewhere. Wherever you are, the promises in this policy apply to you.
            </p>
          </Card>

          {/* The short version */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">The short version</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shortVersion.map((f) => (
                <div key={f.title} className="bg-slate-800/50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-white mb-2">
                    <f.icon className="w-5 h-5 text-purple-300 shrink-0" />
                    <span className="font-semibold">{f.title}</span>
                  </div>
                  <p className="text-sm text-slate-300">{f.description}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* What we collect */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              <span>What we collect, feature by feature</span>
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-purple-300 mb-2">Using the app without an account</h3>
                <p className="text-slate-300">
                  The first time you open the app it asks the Dive Deep Dialogues sign-in service (we call it the
                  auth hub) for an anonymous identity: a random ID that is stored in your browser. That ID is how your
                  writing is backed up and how it comes back if you clear your browser on the same device. It does
                  not contain your name, email, phone number or anything else about you. Signing out disconnects the
                  backup from this device.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">If you create an account</h3>
                <p className="text-slate-300">
                  You can sign up with a username and password, or with an email address and password, plus the name
                  you would like the app to call you. That is all we ask for. These accounts are held by this
                  app&apos;s own sign-in service, not the auth hub, so they work in Friendships Vibe only, and what you
                  write in the tools is not backed up: it stays on the device you wrote it on. We do not ask for your
                  date of birth, school, phone number or address.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">Dive In modules, the tools and the assessment</h3>
                <p className="text-slate-300">
                  What you write in reflections, tool entries and the assessment, plus which parts of the modules you
                  have finished, is saved on your device. If you use the app without an account, your tool entries
                  and assessment results are also backed up to our servers under your random ID. While you are signed
                  in, your progress and the insights you save from the dashboard are kept in this app&apos;s database.
                  Every tool tells you this. Only you can see it in the app.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">Friend Circles</h3>
                <p className="text-slate-300 mb-3">
                  A circle has nowhere to write. If you start or join one we store: the circle&apos;s name and emoji
                  and its invite code; who is in it and the name you show the circle (your app username); which of
                  the modules the circle has picked you have finished (whether, not how far through, and nothing you
                  wrote); and, for each question the circle asks, which of its fixed answers you picked and when.
                  We also store when you joined, and when the circle was started and last used.
                </p>
                <p className="text-slate-300">
                  All of that is visible to the people in that circle and to no one else in the app. Your answer to a
                  question is hidden from everyone but you until everyone asked has answered, or until three days
                  have passed with at least two answers in. Then everyone in the circle who was asked it sees who
                  picked what. Answers are not anonymous. Someone who joins later does not see answers to questions
                  asked before they arrived. Anyone with the circle&apos;s invite code can join it.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">The Coach</h3>
                <p className="text-slate-300">
                  The Coach is an AI, not a person. Before you can use it you are asked to agree to a short consent
                  screen. Your messages, and notes the Coach keeps so it can remember context, are stored in our
                  Knowledge Hub against your ID. To write a reply, the Knowledge Hub sends your message and the
                  relevant notes to Anthropic, whose Claude models generate the answer. Anthropic does not use what
                  we send it to train its models. You can see, change or delete everything the Coach knows about you
                  from the Coach page at any time.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">Technical information</h3>
                <p className="text-slate-300">
                  Like every website, the services that deliver the app (Google and Cloudflare) keep short-lived
                  server logs with your IP address, browser type and the time of each request, for security and to
                  keep the service running. We do not use them to profile you. There is no advertising code in the
                  app.
                </p>
              </div>

              <div>
                <h3 className="text-purple-300 mb-2">Usage analytics</h3>
                <p className="text-slate-300">
                  Google&apos;s Firebase Analytics and Firebase Performance Monitoring run in the app today. They
                  record which screens are opened, when the Vibe Check is started and finished (with its overall
                  score), and how quickly pages load, against a random ID that Google&apos;s software gives your browser. They do not get
                  your name, your email address or anything you write in the tools. None of it is used for
                  advertising and nothing is sold. Our own standard for apps used by teenagers says Google
                  analytics should not run in them, and we are removing it from this app; this page will change
                  when we have.
                </p>
              </div>
            </div>
          </Card>

          {/* How we protect it */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <span>How it is protected, honestly</span>
            </h2>
            {bullets([
              'Everything travels between your device and our servers over an encrypted connection (HTTPS).',
              'On our servers it is stored on Google Cloud, which encrypts it at rest. All three of our services that hold your information (this app, the auth hub and the Knowledge Hub) run in Google\'s Sydney region, Australia.',
              'Data on your own device is stored in your browser\'s normal storage. It is not separately encrypted, so anyone who can use your device and browser could see it. Sign out on shared devices.',
              'This is not end-to-end encryption. Our staff with administrator access could technically read what is stored on our servers. We do not, except to fix a problem you have asked us to fix, and your writing is never linked to a name unless you have given us one by creating an account.',
              'Only a small number of people at SILVER WOLF Productions have that access, and no system is perfectly secure. If we ever have a breach that could affect you, we will tell you and the relevant regulator as the law where you live requires.',
            ], Lock, 'w-4 h-4 text-purple-300 mt-1 shrink-0')}
          </Card>

          {/* Who else sees it */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              <span>Who else sees it</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-purple-300 mb-3">Companies that help us run the app</h3>
                {bullets([
                  'Google (Firebase): hosting, sign-in, our databases, the search behind the Coach, and, for now, the usage analytics described above.',
                  'Anthropic: generates the Coach\'s replies from what the Knowledge Hub sends it.',
                  'Cloudflare: delivers the site and protects it from attacks.',
                  'Each of them only processes your information to provide their service to us.',
                ])}
              </div>
              <div>
                <h3 className="text-purple-300 mb-3">We never</h3>
                {bullets([
                  'Sell your information, or give it to advertisers.',
                  'Use your writing to train AI models, or let anyone else.',
                  'Put you on a marketing list.',
                  'Share it with anyone else, unless the law where we operate requires us to.',
                ], X, 'w-4 h-4 text-red-400 mt-1 shrink-0')}
              </div>
            </div>
          </Card>

          {/* Friend Circles specifics */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>Friend Circles: what to know before you join one</span>
            </h2>
            {bullets([
              'There is nowhere to write in a circle: no posts, comments, reactions or chat. You pick from fixed answers, and that is all a circle keeps of what you think.',
              'Circle answers are not anonymous. When a question reveals, everyone in the circle who was asked it sees who picked what. We keep a circle from outsiders; we cannot stop a friend screenshotting it.',
              'Anyone with the invite code can join, so send it only to people you know, privately. If it gets out, anyone in the circle can swap it for a new one, and the old one stops working straight away.',
              'Nobody can remove anyone else from a circle, including whoever started it. Anyone can leave at any time, and nobody is told.',
              'Leaving deletes your answers and your name from the circle. If you are the last one in, leaving deletes the circle.',
              'Until October 2026 circles had written group reflections, comments and a Question of the Day. Those can no longer be read in the app by anyone, and we are deleting them from our servers. If you wrote any and want to know they have gone, email us.',
            ], Info, 'w-4 h-4 text-purple-300 mt-1 shrink-0')}
          </Card>

          {/* Young people */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Baby className="w-5 h-5" />
              <span>Young people, parents and guardians</span>
            </h2>
            <p className="text-slate-300 mb-3">
              Friendships Vibe is designed for teenagers and young adults, from 13 up. That is why it asks for so little:
              you can use everything in it without giving us a name or an email address. We do not knowingly collect
              personal information from anyone under 13. If you are a parent or guardian and believe a child under 13
              has given us personal information, email us and we will delete it.
            </p>
            <p className="text-slate-300 mb-3">
              Whether a parent or guardian needs to agree before a young person uses a service like this depends on
              where you live. In Australia there is no fixed age; the rule is that we have to make our privacy
              practices understandable to the people we serve, which is what this page is for. Most of the countries
              this app serves set a specific age below which a parent&apos;s agreement is needed: 13 in the UK and the
              US, 16 in Ireland and much of Europe, and rules in Canada, New Zealand and India that turn on a young
              person&apos;s maturity or a parent&apos;s consent. If that applies to you, please read this page with a
              parent or guardian.
            </p>
            <p className="text-slate-300">
              Parents and guardians: you are welcome to email us with questions. Because most people use the app with
              a random ID rather than a name, we usually cannot find a young person&apos;s data from a name alone; we
              will explain what we can do from the device they used.
            </p>
          </Card>

          {/* How long */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>How long we keep it</span>
            </h2>
            {bullets([
              'On your device: until you clear it, from Privacy & Data or by clearing your browser.',
              'Your backup: until you delete it or ask us to. We do not currently delete inactive anonymous backups automatically; email us and we will delete yours.',
              'Your circle answers and your name in a circle: until you leave the circle, which deletes them. A circle is deleted when the last person leaves it.',
              'Your account: until you ask us to close it.',
              'What the Coach knows: until you delete it from the Coach page.',
              'Server logs: kept briefly by Google and Cloudflare for security, then discarded.',
            ], Circle)}
          </Card>

          {/* Your controls and rights */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              <span>Your controls and your rights</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <h3 className="text-purple-300 mb-3">In the app, right now</h3>
                {bullets([
                  { key: 'export', node: <>Download everything you have written as a file, from <button type="button" onClick={() => setCurrentView('data-privacy')} className="text-purple-300 underline underline-offset-2 hover:text-white">Privacy &amp; Data</button>.</> },
                  'Delete what you have written, from this device, from the backup on our servers and from the Coach, on the same page.',
                  'Delete what the Coach knows about you, from the Coach page.',
                  'Sign out, which signs you out of the app and the auth hub and removes your tool writing from this device.',
                ], Check, 'w-4 h-4 text-green-400 mt-1 shrink-0')}
              </div>
              <div>
                <h3 className="text-purple-300 mb-3">By emailing us</h3>
                {bullets([
                  'See a copy of what we hold about you.',
                  'Correct something that is wrong.',
                  'Delete your backup, your circle contributions or your whole account from our servers.',
                  'Object to, or ask us to limit, how we use it.',
                ], Mail, 'w-4 h-4 text-purple-300 mt-1 shrink-0')}
              </div>
            </div>
            <p className="text-slate-300 mb-3">
              Email <a href={`mailto:${HELP}`} className="text-purple-300 hover:underline">{HELP}</a> from the device you
              use the app on if you can, so we can find your random ID. We answer within 30 days, and there is no charge.
            </p>
            <p className="text-slate-300">
              If you are not happy with our answer you can complain to the privacy regulator where you live. In Australia
              that is the Office of the Australian Information Commissioner (OAIC) under the Privacy Act 1988. Most of the
              countries this app serves have an equivalent: the Privacy Commissioner in New Zealand, the Information
              Commissioner&apos;s Office in the UK, the Data Protection Commission in Ireland, the Office of the Privacy
              Commissioner of Canada, the Data Protection Board of India, and in the US your state&apos;s attorney-general
              or the Federal Trade Commission. If you are in the UK, Ireland or elsewhere in Europe, you also have the
              specific rights the UK GDPR and the GDPR give you, and we honour them.
            </p>
          </Card>

          {/* Changes */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl text-white font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span>When this policy changes</span>
            </h2>
            <p className="text-slate-300">
              If we change what we collect or who sees it, we will update the date at the top and, for anything that
              matters, tell you in the app before it takes effect. Smaller wording changes just get the new date.
            </p>
          </Card>

          {/* Terms link */}
          <div className="text-center mb-8">
            <h3 className="text-purple-300 mb-4">Related information</h3>
            <a
              href="https://divedeepdialogues.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600/20 text-purple-300 rounded-full hover:bg-purple-600/30 transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
              <span>The Dive Deep Dialogues Terms &amp; Conditions</span>
            </a>
          </div>

          {/* Footer */}
          <Card className="p-6 mb-8 text-center">
            <p className="text-slate-400 mb-4">
              Questions about privacy? <a href={`mailto:${HELP}`} className="text-purple-300 hover:underline">{HELP}</a>
            </p>
            <p className="text-slate-400">Operated by SILVER WOLF Productions (ABN 66 376 765 633), Australia</p>
            <p className="text-slate-400 mt-2">© 2026 SILVER WOLF Productions. All rights reserved.</p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
